import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QrCode, X, Sparkles, Crown, Clock } from "lucide-react";
import { WEEKDAYS, normaliseWeek, fmtTime12, todayStatus } from "@/lib/businessHours";

/**
 * Shown at the top of the list column when a consumer landed here via
 * a business's QR poster (?biz=<id>). Two goals:
 *   1. Confirm which business they scanned (with a gold accent so it feels
 *      like the "premium" QR poster the business printed).
 *   2. Offer a low-friction "Follow" — phone-only, stored as an intent that
 *      the mobile app resolves into a real follow on next install/login.
 *
 * Everything else on the page stays fully interactive — the banner is a
 * pointer, not a paywall.
 */
export default function ScannedBusinessBanner({ business, onDismiss }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/follows/intent", {
        business_id: business.id,
        phone: phone.trim(),
      });
      setDone(true);
      toast.success(`You'll follow ${data.business_name} in the app`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="scanned-biz-banner"
      className="border-b border-border bg-gradient-to-r from-[#1c1c1c] to-[#2A2724] text-white p-5 md:px-8"
    >
      <div className="flex items-start gap-3">
        <QrCode size={18} className="text-[#C9A85F] mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#C9A85F]">
            You scanned
          </p>
          <p
            data-testid="scanned-biz-name"
            className="mt-0.5 text-lg leading-tight tracking-tight truncate flex items-center gap-2"
          >
            <span className="truncate">{business.business_name || business.name}</span>
            {business.is_platinum && (
              <span
                data-testid="scanned-biz-platinum-crown"
                aria-label="Platinum business"
                title="Platinum business"
                className="inline-flex flex-shrink-0 items-center justify-center gold-metallic-bg h-5 w-5 rounded-sm"
              >
                <Crown size={11} className="text-[#2A2724]" strokeWidth={2.5} />
              </span>
            )}
          </p>
          {business.address && (
            <p className="mt-0.5 text-xs text-white/60 truncate">
              {business.address}
            </p>
          )}
        </div>
        <button
          data-testid="scanned-biz-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="h-9 w-9 shrink-0 inline-flex items-center justify-center border border-white/20 hover:border-white/50 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {done ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[#C9A85F]">
          <Sparkles size={14} />
          <span>
            Nice — you'll follow them when you open the mobile app. In the
            meantime, keep exploring below.
          </span>
        </div>
      ) : (
        <form
          onSubmit={submit}
          data-testid="scanned-biz-follow-form"
          className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center"
        >
          <label className="sr-only" htmlFor="follow-phone">
            Phone number
          </label>
          <input
            id="follow-phone"
            data-testid="scanned-biz-phone-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Your phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-11 sm:flex-1 rounded-none bg-white/5 border border-white/20 px-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#C9A85F]"
          />
          <button
            type="submit"
            data-testid="scanned-biz-follow-btn"
            disabled={busy || !phone.trim()}
            className="h-11 shrink-0 inline-flex items-center justify-center gap-1.5 gold-metallic-bg text-black px-5 text-[11px] font-semibold uppercase tracking-widest active:scale-[0.98] transition-transform disabled:opacity-60"
          >
            {busy ? "…" : "Follow"}
          </button>
        </form>
      )}

      <p className="mono mt-3 text-[10px] uppercase tracking-widest text-white/40">
        Their openings are marked in gold below · keep browsing all businesses
      </p>

      <HoursSchedule hours={business.business_hours} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Full 7-day schedule, collapsed by default with a "today" summary showing.
// Kept inside this file to keep the banner self-contained.
// -----------------------------------------------------------------------------
function HoursSchedule({ hours }) {
  const week = normaliseWeek(hours);
  const anySet = Object.values(week).some((d) => !d.closed || d.by_appointment);
  const [open, setOpen] = useState(false);
  const status = todayStatus(hours);
  if (!anySet) return null;

  const todayIdx = (new Date().getDay() + 6) % 7; // 0=Mon..6=Sun for our WEEKDAYS array

  return (
    <div data-testid="scanned-biz-hours" className="mt-4 border-t border-white/10 pt-3">
      <button
        type="button"
        data-testid="scanned-biz-hours-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A85F]"
      >
        <span className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60">
          <Clock size={11} className="text-[#C9A85F]" /> {status.label || "Weekly hours"}
        </span>
        <span className="mono text-[10px] uppercase tracking-widest text-[#C9A85F]">
          {open ? "Hide" : "See week"}
        </span>
      </button>
      {open && (
        <ul data-testid="scanned-biz-hours-list" className="mt-3 grid gap-1">
          {WEEKDAYS.map((d, i) => {
            const row = week[d.key];
            let value;
            if (row.by_appointment) value = "By appointment";
            else if (row.closed || !row.open || !row.close) value = "Closed";
            else value = `${fmtTime12(row.open)} – ${fmtTime12(row.close)}`;
            const isToday = i === todayIdx;
            return (
              <li
                key={d.key}
                data-testid={`scanned-biz-hours-${d.key}`}
                className={`mono flex items-center justify-between border-l-2 pl-3 text-[11px] ${
                  isToday ? "border-[#C9A85F] text-white" : "border-white/10 text-white/60"
                }`}
              >
                <span className="uppercase tracking-widest">{d.label}</span>
                <span>{value}</span>
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
}